import React, { useState, useEffect } from 'react';
import {
    Card,
    CardContent,
    CardHeader,
    Typography,
    Box,
    TextField,
    Button,
    Chip,
    Paper,
    CircularProgress,
    Divider,
    IconButton,
    Tooltip,
    Stack,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import { request } from '../../../../utils/request'; // Adjust path as needed
import { toast } from 'react-toastify';
import { PriceCodeData } from '../../types';

const PriceCode: React.FC = () => {
    const [priceCodeData, setPriceCodeData] = useState<PriceCodeData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [updating, setUpdating] = useState<boolean>(false);
    const [editingDigit, setEditingDigit] = useState<number | null>(null);
    const [editCharacter, setEditCharacter] = useState<string>('');
    const [testNumber, setTestNumber] = useState<string>('');
    const [testResult, setTestResult] = useState<string>('');

    // Fetch price code mappings
    const fetchPriceCodeMappings = async () => {
        try {
            setLoading(true);
            const res = await request({
                url: 'priceCode/get-all-priceCode', // Adjust endpoint as needed
                method: 'GET',
            });

            if (res.status === 'success') {
                setPriceCodeData(res.data);
                localStorage.setItem('priceCode_mappings', JSON.stringify(res.data.mappings))
            } else {
                throw new Error(res?.message || 'Failed to fetch price code mappings');
            }
        } catch (error: any) {
            toast.error(error || 'Failed to load price code mappings');
        } finally {
            setLoading(false);
        }
    };

    // Update character for a digit
    const updateCharacter = async (digit: number, character: string) => {
        try {
            setUpdating(true);
            const res = await request({
                url: 'priceCode/update-priceCode', // Adjust endpoint as needed
                method: 'PUT',
                data: { digit, character },
            });

            if (res.status === 'success') {
                setPriceCodeData(res.data);
                toast.success('Character updated successfully');
                setEditingDigit(null);
                setEditCharacter('');
                fetchPriceCodeMappings();
            } else {
                throw new Error(res?.message || 'Failed to update character');
            }
        } catch (error: any) {
            toast.error(error || 'Failed to update character');
        } finally {
            setUpdating(false);
        }
    };

    // Test number encoding
    const testNumberEncoding = async () => {
        if (!testNumber.trim()) return;

        try {
            const res = await request({
                url: `priceCode/get-code-for-number/${testNumber}`,
                method: 'GET',
            });

            if (res.status === 'success') {
                setTestResult(res.data.code);
            } else {
                throw new Error(res.message || 'Failed to encode number');
            }
        } catch (error: any) {
            toast.error(error || 'Failed to encode number');
            setTestResult('');
        }
    };

    const handleEditStart = (digit: number, currentCharacter: string) => {
        setEditingDigit(digit);
        setEditCharacter(currentCharacter);
    };

    const handleEditCancel = () => {
        setEditingDigit(null);
        setEditCharacter('');
    };

    const handleEditSave = () => {
        if (editingDigit !== null && editCharacter.trim()) {
            updateCharacter(editingDigit, editCharacter.trim());
        }
    };

    const handleTestNumber = () => {
        testNumberEncoding();
    };

    useEffect(() => {
        fetchPriceCodeMappings();
    }, []);

    if (loading) {
        return (
            <Card elevation={3}>
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress size={40} />
                    <Typography variant="body1" sx={{ mt: 2 }}>
                        Loading price code mappings...
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    return (
        <>
            <Card elevation={3} sx={{ borderRadius: 1 }}>
                <CardHeader
                    title={
                        <Box display="flex" alignItems="center" gap={2} >
                            <MonetizationOnIcon color="primary" sx={{ fontSize: "30px" }} />
                            <Typography fontWeight={600} sx={{ fontSize: "30px" }}>
                                Price Code Mappings
                            </Typography>
                        </Box>
                    }
                    subheader="Manage digit to character mappings for price codes"
                    action={
                        <Tooltip title="Refresh mappings" arrow placement='left'>
                            <IconButton onClick={fetchPriceCodeMappings} disabled={updating}>
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                    }
                />

                <CardContent>
                    {/* Current Mappings */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h6" gutterBottom fontWeight={600} color="primary">
                            Current Mappings
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Map digits (0-9) to characters for encoding numbers
                        </Typography>

                        {/* Mappings Container */}
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: "row",
                                flexWrap: 'wrap',
                                gap: 2,
                                justifyContent: 'start',
                            }}
                        >
                            {priceCodeData?.mappings.map((mapping) => (
                                <Paper
                                    key={mapping.digit}
                                    variant="outlined"
                                    sx={{
                                        p: 2,
                                        borderRadius: 1,
                                        borderColor: editingDigit === mapping.digit ? 'primary.main' : 'grey.200',
                                        backgroundColor: editingDigit === mapping.digit ? 'primary.50' : 'white',
                                        transition: 'all 0.2s ease',
                                        minWidth: "23%",
                                        maxWidth: "23%",
                                        flex: '1 0 calc(25% - 16px)',

                                    }}
                                >
                                    <Box display="flex" alignItems="center" justifyContent="space-between">
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Chip
                                                label={mapping.digit}
                                                color="primary"
                                                variant="filled"
                                                sx={{ fontWeight: 800, minWidth: 40 }}
                                            />
                                            <Typography variant="h6" sx={{ mx: 1 }}>
                                                →
                                            </Typography>
                                            {editingDigit === mapping.digit ? (
                                                <TextField
                                                    size="small"
                                                    value={editCharacter}
                                                    onChange={(e) => setEditCharacter(e.target.value.toUpperCase())}
                                                    inputProps={{
                                                        maxLength: 1,
                                                        style: { textAlign: 'center', fontWeight: 600, fontSize: '1.1rem' },
                                                    }}
                                                    sx={{ width: 60 }}
                                                    autoFocus
                                                />
                                            ) : (
                                                <Chip
                                                    label={mapping.character}
                                                    color="secondary"
                                                    variant="filled"
                                                    sx={{
                                                        fontWeight: 600,
                                                        fontSize: '1.1rem',
                                                        minWidth: 40,
                                                        height: 32,
                                                    }}
                                                />
                                            )}
                                        </Box>

                                        <Box display="flex" gap={0.5}>
                                            {editingDigit === mapping.digit ? (
                                                <>
                                                    <Tooltip title="Save">
                                                        <IconButton
                                                            size="small"
                                                            color="success"
                                                            onClick={handleEditSave}
                                                            disabled={updating || !editCharacter.trim()}
                                                        >
                                                            <SaveIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Cancel">
                                                        <IconButton size="small" color="error" onClick={handleEditCancel}>
                                                            <CancelIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            ) : (
                                                <Tooltip title="Edit character">
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => handleEditStart(mapping.digit, mapping.character)}
                                                        disabled={updating}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </Box>
                                </Paper>
                            ))}
                        </Box>

                        {priceCodeData?.updatedAt && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                                Last updated: {new Date(priceCodeData.updatedAt).toLocaleString()}
                            </Typography>
                        )}
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    {/* Test Number Encoding Section */}
                    <Box>
                        <Typography variant="h6" gutterBottom fontWeight={600} color="primary">
                            Test Number Encoding
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Enter a number to see how it gets encoded with current mappings
                        </Typography>

                        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                            <TextField
                                label="Enter number"
                                variant="outlined"
                                size="small"
                                value={testNumber}
                                onChange={(e) => {
                                    const value = e.target.value.replace(/[^0-9]/g, '');
                                    setTestNumber(value);
                                }}
                                placeholder="e.g., 12345"
                                sx={{ minWidth: 200 }}
                                inputProps={{ maxLength: 10 }}
                            />
                            <Button
                                variant="contained"
                                onClick={handleTestNumber}
                                disabled={!testNumber.trim() || updating}
                                startIcon={<MonetizationOnIcon />}
                            >
                                Encode
                            </Button>
                            {testResult && <Box display="flex" alignItems="center" gap={2}>
                                <Typography variant="h6" fontWeight={600} color="success.main">
                                    {testNumber} → {testResult}
                                </Typography>
                                <Chip label="Encoded" color="success" variant="outlined" size="small" />
                            </Box>
                            }
                        </Stack>
                    </Box>
                </CardContent>
            </Card>

        </>
    );
};

export default PriceCode;